import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  alpha,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { Logo } from "assets/index";
import HamburgerIcon from "assets/svg/HamburgerIcon";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import theme, { responsiveFontSize } from "themes/Theme";

interface IProps {
  toggleDrawer: () => void;
  open: boolean;
  isMobile: boolean;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Header: React.FC<IProps> = ({ toggleDrawer, open, isMobile }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        ...styles.container,
        backgroundColor: scrolled
          ? theme.palette.background.default
          : "transparent",
        boxShadow: scrolled
          ? "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px"
          : "none",
      }}
    >
      <Container component={"header"} sx={styles.headerSection}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {isMobile ? (
            <IconButton onClick={toggleDrawer}>
              <HamburgerIcon color={scrolled ? "#000000" : "#FFFFFF"} />
            </IconButton>
          ) : null}

          <Box
            component={"img"}
            src={Logo}
            sx={styles.logoIcon}
            onClick={() => navigate("/")}
          />
        </Stack>

        {isMobile ? (
          <Box />
        ) : (
          <Box component={"nav"} sx={styles.navContainer}>
            <Link
              to="/"
              style={
                pathname === "/" ? styles.activeLinkStyle : styles.linkStyle
              }
            >
              Home
            </Link>
            <Link
              to="/about-us"
              style={
                pathname === "/about-us"
                  ? styles.activeLinkStyle
                  : styles.linkStyle
              }
            >
              About us
            </Link>
            <Link
              to="/faq"
              style={
                pathname === "/faq" ? styles.activeLinkStyle : styles.linkStyle
              }
            >
              FAQs
            </Link>
            <Link
              to="/contact-us"
              style={
                pathname === "/contact-us"
                  ? styles.activeLinkStyle
                  : styles.linkStyle
              }
            >
              Contact us
            </Link>
          </Box>
        )}
        {isMobile && (
          <Drawer
            open={open}
            onClose={toggleDrawer}
            sx={{
              width: "60%",
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: "60%",
                boxSizing: "border-box",
                backgroundColor: "#FFFFFF",
              },
            }}
            variant="persistent"
            anchor="left"
          >
            <DrawerHeader>
              <Box
                component={"img"}
                src={Logo}
                sx={{ ...styles.logoIcon, width: 40, height: 40 }}
              />
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: responsiveFontSize(24),
                }}
              >
                ERC
              </Typography>

              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon sx={{ color: theme.palette.primary.main }} />
              </IconButton>
            </DrawerHeader>
            <Divider />
            <Box
              sx={{ px: 2, display: "flex", flexDirection: "column", mt: 2 }}
            >
              <Link
                to="/"
                onClick={toggleDrawer}
                style={
                  pathname === "/"
                    ? styles.drawerActiveLinkStyle
                    : styles.drawerLinkStyle
                }
              >
                Home
              </Link>
              <Link
                to="/about-us"
                onClick={toggleDrawer}
                style={
                  pathname === "/about-us"
                    ? styles.drawerActiveLinkStyle
                    : styles.drawerLinkStyle
                }
              >
                About us
              </Link>
              <Link
                to="/faq"
                onClick={toggleDrawer}
                style={
                  pathname === "/faq"
                    ? styles.drawerActiveLinkStyle
                    : styles.drawerLinkStyle
                }
              >
                FAQs
              </Link>
              <Link
                to="/contact-us"
                onClick={toggleDrawer}
                style={
                  pathname === "/contact-us"
                    ? styles.drawerActiveLinkStyle
                    : styles.drawerLinkStyle
                }
              >
                Contact us
              </Link>
            </Box>
          </Drawer>
        )}

        <Box sx={styles.loginContainer}>
          <Button variant="text" onClick={() => navigate("/auth")}>
            <Typography
              sx={{
                ...styles.loginBtnText,
                color: scrolled
                  ? theme.palette.text.primary
                  : theme.palette.common.white,
              }}
            >
              Login
            </Typography>
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
export default Header;
const styles = {
  container: {
    transition: "background-color 0.35s ease",
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    zIndex: 999,
    width: "100%",
  },
  headerSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 5px",
  },
  logoText: {
    fontSize: responsiveFontSize(24),
    fontWeight: 400,
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: theme.palette.primary.light,
    "& span": {
      color: theme.palette.primary.main,
    },
    "&:hover": {
      cursor: "pointer",
    },
  },
  navContainer: {
    backgroundColor: alpha(theme.palette.text.primary, 0.75),
    borderRadius: "3em",
    display: "flex",
    flexDirection: "row",
    px: 4,
    py: 1,
    gap: 5,
  },
  loginContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  logoIcon: {
    width: 60,
    height: 60,
    objectFit: "contain",
    "&:hover": {
      cursor: "pointer",
    },
  },
  linkStyle: {
    color: theme.palette.custom?.white,
    fontWeight: 400,
    textDecoration: "none",
    fontSize: responsiveFontSize(14),
  },
  activeLinkStyle: {
    color: theme.palette.primary.light,
    fontWeight: 400,
    textDecoration: "none",
    fontSize: responsiveFontSize(14),
  },
  drawerLinkStyle: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    textDecoration: "none",
    fontSize: responsiveFontSize(16),
    marginBottom: "5px",
    padding: "8px 15px",
  },
  drawerActiveLinkStyle: {
    color: "#FFFFFF",
    fontWeight: 400,
    textDecoration: "none",
    fontSize: responsiveFontSize(16),
    marginBottom: "5px",
    padding: "8px 15px",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "6px",
  },
  loginBtnText: {
    color: theme.palette.custom.white,
    fontWeight: 400,
    fontSize: responsiveFontSize(18),
    textTransform: "none",
  },
};
